import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { CURRENCY } from '../config/constants';
import { productsAPI } from '../api/client';

const OTHERS = 'Others';

const isFreeTextMaterial = (name) => {
  const key = String(name || '').trim().toLowerCase();
  return key === 'other' || key === 'others';
};

/** Catalog names + legacy line-item materials + synthetic "Others" (once). */
const buildMaterialOptions = (catalog, items = []) => {
  const seen = new Set();
  const names = [];
  const add = (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(trimmed);
  };

  (catalog || []).forEach((p) => add(p.name));
  (items || []).forEach((item) => {
    if (item.material_type && !isFreeTextMaterial(item.material_type)) {
      add(item.material_type);
    }
  });
  if (!seen.has('other') && !seen.has('others')) names.push(OTHERS);
  return names;
};

// Fallback catalog (used if the API is unreachable or returns nothing) — mirrors
// the values the backend seeds the product_catalog table with.
const FALLBACK_CATALOG = [
  { name: 'RMC', sub_item_label: 'RMC grade', sub_items: ['Grade 10', 'Grade 15', 'Grade 20', 'Grade 25', 'Grade 30', 'Grade 35', 'Grade 40'].map((value) => ({ value })) },
  { name: 'Aggregates', sub_item_label: 'Aggregate type', sub_items: ['M sand', '40 mm', '20 mm', '12 mm', '6 mm', 'P sand'].map((value) => ({ value })) },
  { name: 'AAC Site Brick', sub_item_label: 'Brick Size', sub_items: ['4" Inch Brick', '6" Inch Brick', '8" Inch Brick', '9" Inch Brick'].map((value) => ({ value })) },
];

export const emptyLineItem = () => ({
  material_type: '',
  sub_item: '',
  product_name: '',
  quantity: '',
  volume_unit: 'm3',
  unit_price: '',
  boom_pump_amount: '',
  required_date: '',
});

export const calcLineTotal = (item) => {
  const qty = parseFloat(item.quantity) || 0;
  const price = parseFloat(item.unit_price) || 0;
  const boom = parseFloat(item.boom_pump_amount) || 0;
  return qty * price + boom;
};

export const calcOrderTotalFromItems = (items) =>
  (items || []).reduce((sum, item) => sum + calcLineTotal(item), 0);

const buildProductName = (item) => {
  const material = (item.material_type || '').trim();
  const sub = (item.sub_item || '').trim();
  if (material && sub) return `${material} ${sub}`;
  if (item.product_name?.trim()) return item.product_name.trim();
  if (material) return material;
  return 'Item';
};

export const lineItemsToApiPayload = (items) =>
  (items || [])
    .filter((item) => item.material_type || item.product_name?.trim())
    .map((item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unit_price) || 0;
      return {
        product_name: buildProductName(item),
        material_type: item.material_type || null,
        volume_unit: item.volume_unit || 'm3',
        quantity,
        unit_price: unitPrice,
        total_price: calcLineTotal(item),
        boom_pump_amount: parseFloat(item.boom_pump_amount) || null,
        required_date: item.required_date || null,
        requirements_json: JSON.stringify({
          material_type: item.material_type || '',
          sub_item: item.sub_item || '',
          product_name: item.product_name || '',
        }),
      };
    });

export const parseLineItemFromApi = (row) => {
  let meta = {};
  if (row.requirements_json) {
    try {
      meta = typeof row.requirements_json === 'string'
        ? JSON.parse(row.requirements_json)
        : row.requirements_json;
    } catch {
      meta = {};
    }
  }

  const materialTypeRaw = meta.material_type || row.material_type || '';
  const materialType = isFreeTextMaterial(materialTypeRaw) ? OTHERS : materialTypeRaw;
  // New generic field, then legacy specific fields.
  let subItem = meta.sub_item || meta.rmc_grade || meta.aggregate_type || meta.aac_size || '';
  let productName = meta.product_name || '';

  // Derive sub-item / product name from the saved label for legacy rows without meta.
  const fullName = row.product_name || '';
  if (!subItem && !productName && fullName) {
    if (materialType && !isFreeTextMaterial(materialType) && fullName.startsWith(`${materialType} `)) {
      subItem = fullName.slice(materialType.length + 1);
    } else {
      productName = fullName;
    }
  }

  return {
    material_type: materialType,
    sub_item: subItem,
    product_name: productName,
    quantity: row.quantity != null ? String(row.quantity) : '',
    volume_unit: row.volume_unit || 'm3',
    unit_price: row.unit_price != null ? String(row.unit_price) : '',
    boom_pump_amount: row.boom_pump_amount != null ? String(row.boom_pump_amount) : '',
    required_date: row.required_date ? String(row.required_date).slice(0, 10) : '',
  };
};

export const formatLineItemsSummary = (order) => {
  const items = order?.items;
  if (items?.length) {
    return items.map((i) => {
      const qty = parseFloat(i.quantity) || 0;
      const unit = i.volume_unit || 'm3';
      return `${i.product_name} (${qty} ${unit})`;
    }).join('; ');
  }
  return order?.product_details?.trim() || '—';
};

const OrderLineItemsEditor = ({ items, onChange, disabled = false }) => {
  const list = items?.length ? items : [emptyLineItem()];
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);

  useEffect(() => {
    let active = true;
    productsAPI
      .getActive()
      .then((res) => {
        const data = res.data?.data;
        if (active && Array.isArray(data) && data.length) {
          setCatalog(data);
        }
      })
      .catch(() => {
        // Keep the fallback catalog on failure.
      });
    return () => {
      active = false;
    };
  }, []);

  const findProduct = (name) => catalog.find((p) => p.name === name);

  // Material options = catalog names + any material already on an item (e.g. a
  // hidden/deleted product on an existing order) + the generic "Others" once.
  const materialOptions = buildMaterialOptions(catalog, list);

  const updateItem = (index, patch) => {
    const next = list.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const addItem = () => onChange([...list, emptyLineItem()]);

  const removeItem = (index) => {
    if (list.length <= 1) {
      onChange([emptyLineItem()]);
      return;
    }
    onChange(list.filter((_, i) => i !== index));
  };

  const orderTotal = calcOrderTotalFromItems(list);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-gray-800">Order line items</h4>
        <span className="text-sm font-semibold text-gray-700">
          Total: {CURRENCY.SYMBOL}{orderTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>

      <div className="space-y-3">
        {list.map((item, index) => {
          const product = findProduct(item.material_type);
          const subItemOptions = product?.sub_items?.map((s) => s.value) || [];
          // Make sure a previously-saved sub-item value still shows even if it
          // was later removed from the catalog.
          if (item.sub_item && !subItemOptions.includes(item.sub_item)) {
            subItemOptions.push(item.sub_item);
          }
          const hasSubItems = subItemOptions.length > 0;
          const isFreeText = item.material_type && item.material_type !== '' && !hasSubItems;

          return (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-gray-500 uppercase">Item {index + 1}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="text-red-600 hover:text-red-700 p-1"
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Material *</label>
                  <select
                    value={item.material_type}
                    onChange={(e) => updateItem(index, {
                      material_type: e.target.value,
                      sub_item: '',
                      product_name: '',
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    disabled={disabled}
                    required
                  >
                    <option value="">— Select —</option>
                    {materialOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>

                {hasSubItems && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      {product?.sub_item_label || 'Option'}
                    </label>
                    <select
                      value={item.sub_item}
                      onChange={(e) => updateItem(index, { sub_item: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      disabled={disabled}
                    >
                      <option value="">— {product?.sub_item_label || 'Select'} —</option>
                      {subItemOptions.map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </select>
                  </div>
                )}

                {isFreeText && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Product name</label>
                    <input
                      type="text"
                      value={item.product_name}
                      onChange={(e) => updateItem(index, { product_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Product description"
                      disabled={disabled}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Quantity *</label>
                  <div className="flex gap-1">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, { quantity: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      disabled={disabled}
                      required
                    />
                    <select
                      value={item.volume_unit}
                      onChange={(e) => updateItem(index, { volume_unit: e.target.value })}
                      className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
                      disabled={disabled}
                    >
                      <option value="m3">m³</option>
                      <option value="tons">tons</option>
                      <option value="units">units</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Rate / unit ({CURRENCY.CODE}) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.unit_price}
                    onChange={(e) => updateItem(index, { unit_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    disabled={disabled}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Boom / pump ({CURRENCY.CODE})</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.boom_pump_amount}
                    onChange={(e) => updateItem(index, { boom_pump_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    disabled={disabled}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Required date</label>
                  <input
                    type="date"
                    value={item.required_date}
                    onChange={(e) => updateItem(index, { required_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    disabled={disabled}
                  />
                </div>

                <div className="flex items-end">
                  <p className="text-sm font-semibold text-gray-800 pb-2">
                    Line total: {CURRENCY.SYMBOL}{calcLineTotal(item).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!disabled && (
        <button
          type="button"
          onClick={addItem}
          className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold text-sm"
        >
          <Plus className="w-4 h-4" />
          Add another item
        </button>
      )}
    </div>
  );
};

export default OrderLineItemsEditor;
